import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import api from './../api/axios';
import Userfront from '../auth/userfrontCompat';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faEye, faEnvelope } from '@fortawesome/free-solid-svg-icons';

const ProgressBar = ({ label, value, max, icon }) => {
  const percent = Math.min((value / max) * 100, 100);

  let barColor = 'bg-blue-500';
  if (percent >= 100) barColor = 'bg-red-500';
  else if (percent >= 80) barColor = 'bg-yellow-400';

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-0.5">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <FontAwesomeIcon icon={icon} className="text-gray-500 text-xs" />
          <span>{label}</span>
        </div>
        <span className="text-xs text-gray-500">{value} / {max}</span>
      </div>
      <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const LimitsChart = () => {
  const email = Userfront.user.email;
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchLimitsData = async () => {
      try {
        const userRes = await api.get(`/user/user`);
        const user = userRes.data.user;

        const limits = {
          invites: user.connectionLimit || 40,
          views: user.profileViewLimit || 40,
          messages: user.messagesLimit || 40,
        };

        const logsRes = await api.get(`/user/logs/`);
        const logs = logsRes.data;

        const todayStart = dayjs().startOf('day').valueOf();
        const todayEnd = dayjs().endOf('day').valueOf();

        const counts = { invites: 0, views: 0, messages: 0 };

        logs.forEach(log => {
          const ts = new Date(log.timestamp).getTime();
          if (ts >= todayStart && ts <= todayEnd) {
            if (log.action === 'viewProfile') counts.views += 1;
            else if (log.action === 'sendConnectionRequest') counts.invites += 1;
            else if (log.action === 'sendMessage') counts.messages += 1;
          }
        });

        setData({ limits, counts });
      } catch (error) {
        console.error('Error loading limits chart data', error);
      }
    };

    fetchLimitsData();
  }, [email]);

  if (!data) {
    return <div className="text-sm text-gray-500">{t('home.loading')}</div>;
  }

  const { limits, counts } = data;

  return (
    <div className="p-1.5">
      <ProgressBar
        label={t('home.invites')}
        value={counts.invites}
        max={limits.invites}
        icon={faUserPlus}
      />
      <ProgressBar
        label={t('home.profileViews')}
        value={counts.views}
        max={limits.views}
        icon={faEye}
      />
      <ProgressBar
        label={t('home.messages')}
        value={counts.messages}
        max={limits.messages}
        icon={faEnvelope}
      />
    </div>
  );
};

export default LimitsChart;
